import { useContext, useState } from "react";
import { useSelector } from "react-redux";

import { Button, Group, Paper, Title } from "@mantine/core";
import { UseFormReturnType } from "@mantine/form";
import { useDisclosure } from "@mantine/hooks";

import { IconPlus } from "@tabler/icons-react";

import { ProfileContext } from "@/contexts/profile";
import {
  EncounterAnswersFormType,
  EncounterDiagnosesFormType,
} from "@/encounters/zod-schemas/encounter-answers";
import { NewbornIPPEncounterFormType } from "@/encounters/zod-schemas/newborn-ipp-encounter";
import PermissionGate from "@/permission/components/PermissionGate";
import { PROVIDER_BILLER_SCRIBE_PERMISSION_FLAGS } from "@/permission/constants/practitioner-permissions";
import { selectAllActiveProcedures } from "@/store/slices/procedures";

import DxDiagnosisDrawer from "./DxDiagnosisDrawer";
import DxPlanList from "./DxPlanList";
import EmptyDiagnosesList from "./EmptyDiagnosesList";

interface DxPlanDiagnosesSectionProps {
  form: UseFormReturnType<EncounterAnswersFormType | NewbornIPPEncounterFormType>;
  isEncounterActive: boolean;
  isAmendment?: boolean;
  onProcedureDiagnosisEditClick?: () => void;
}

/**
 * The Diagnoses section as the Dx tab has always shown it: the diagnoses held in the encounter
 * answers form, plus the ones attached to the objective tab's procedures.
 *
 * Everything this list needs is owned here — the add/edit drawer, the diagnosis being edited, and the
 * procedures read from the store. That is what lets the billing pipeline flag swap one component for
 * another rather than branch inside the tab: with the flag on, none of this mounts. See
 * `EncounterDiagnoses` for the Condition-driven section that replaces it.
 */
export default function DxPlanDiagnosesSection({
  form,
  isEncounterActive,
  isAmendment,
  onProcedureDiagnosisEditClick,
}: DxPlanDiagnosesSectionProps) {
  const [isDiagnosisDrawerOpened, { open: openDiagnosisDrawer, close: closeDiagnosisDrawer }] =
    useDisclosure(false);
  const { isPatient } = useContext(ProfileContext);
  const procedures = useSelector(selectAllActiveProcedures);
  const [selectedDiagnosisToEdit, setSelectedDiagnosisToEdit] = useState<
    EncounterDiagnosesFormType | undefined
  >(undefined);

  const diagnoses = form.values.encounterClientDiagnoses ?? [];
  const hasDiagnoses = diagnoses.length > 0 || procedures.length > 0;
  // A locked encounter shows its diagnoses but offers no actions on them. A client sees their own
  // diagnoses but never acts on them, regardless of the encounter's state.
  const canEditDiagnoses = (isEncounterActive || Boolean(isAmendment)) && !isPatient;

  const handleDeleteDiagnosis = (diagnosis: EncounterDiagnosesFormType) => {
    form.setFieldValue(
      "encounterClientDiagnoses",
      diagnoses.filter((d) => d !== diagnosis),
    );
  };

  const handleEditDiagnosis = (diagnosis: EncounterDiagnosesFormType) => {
    setSelectedDiagnosisToEdit(diagnosis);
    openDiagnosisDrawer();
  };

  const handleCloseDiagnosisDrawer = () => {
    setSelectedDiagnosisToEdit(undefined);
    closeDiagnosisDrawer();
  };

  return (
    <>
      <DxDiagnosisDrawer
        form={form}
        opened={isDiagnosisDrawerOpened}
        onClose={handleCloseDiagnosisDrawer}
        diagnosisToEdit={selectedDiagnosisToEdit}
      />

      <Paper p={24} radius="lg">
        <Group pb="md" align="center" justify="space-between">
          <Title order={3} c="burgundy.9">
            Diagnoses
          </Title>
          {hasDiagnoses && canEditDiagnoses && (
            <PermissionGate requiredPermissions={PROVIDER_BILLER_SCRIBE_PERMISSION_FLAGS}>
              <Button
                variant="outline"
                color="burgundy.9"
                onClick={openDiagnosisDrawer}
                data-testid="add-diagnosis-button"
                leftSection={<IconPlus size={14} />}
              >
                Add More
              </Button>
            </PermissionGate>
          )}
        </Group>
        {hasDiagnoses ? (
          <DxPlanList
            diagnoses={diagnoses}
            canEditDiagnoses={canEditDiagnoses}
            handleEditDiagnosis={handleEditDiagnosis}
            handleDeleteDiagnosis={handleDeleteDiagnosis}
            onProcedureDiagnosisEditClick={onProcedureDiagnosisEditClick}
          />
        ) : (
          <EmptyDiagnosesList
            canEditDiagnoses={canEditDiagnoses}
            handleOpenDiagnosisDrawer={openDiagnosisDrawer}
          />
        )}
      </Paper>
    </>
  );
}
